import { flowRight } from 'lodash/fp';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { lifecycle, branch, renderNothing } from 'recompose';
import { change, formValues, getFormValues, reduxForm } from 'redux-form';

import { filterRecommendationsCIF } from 'common/utils/recommendations';
import validateIncomesForReco from 'common/formValidation/rip/taxesApprovalCifValidation';
import withRouter from '../../withRouter';
import getIsValidator from '../../../actions/isValidator';
import dataProvider from '../../dataProvider';
import RipForm from '../Form';
import { POST } from '../../../utils/httpMethods';
import Download from '../Download';
import Comment from './Comment';
import Recommendations from './Recommendations';
import { withSession } from '../../sessions';
import ChoiceResponse from './ChoiceResponse';
import ProcessedRequest from './ProcessedRequest';
import EnveloppeResponse from './EnveloppeResponse';
import ChoiceContact from './ChoiceContact';
import RipTaxIncomes from './TaxIncomesListForRecommendations';
import ESigne from './Esigne';
import SCPISimulation from './SCPISimulation';
import alertEmail from '../../../utils/alertEmail';
import { getTypeRecommendations } from '../../../utils/recommendations';

const SCPI_CIF_TYPES = ['SCPIRendement', 'SCPIaCredit', 'SCPIDemembreeCIF'];

const FormInner = ({ rip, session, isErrors, isApproved, approvalCifBodyForm, relaunchConfirmed, ...props }) => {
    const { target, recommendationId } = props.params;
    const { instanceTheseis } = session;
    const [isValuesCompleted, setIsValuesCompleted] = useState(false);
    const [isSimulationComplete, setIsSimulationComplete] = useState(false);
    const [isGenerated, setIsGenerated] = useState(false);

    let rawRecommendationsCif;
    if ('client' === target) {
        rawRecommendationsCif = rip.recommendationsCIF;
    } else if ('clientAndPartner' === target) {
        rawRecommendationsCif = rip.recommendationsCIFClientAndPartner;
    } else {
        rawRecommendationsCif = rip.partner && rip.partner.recommendationsCIF;
    }

    let recommendationCif = filterRecommendationsCIF(rawRecommendationsCif);

    // Entrée en base de la préconisation ciblée par le lien (isApproved : null = non traitée, true = validée, false = refusée)
    const targetedRecommendation = recommendationId && Object.values(rawRecommendationsCif || {})
        .find(({ data }) => data && data.id === recommendationId);
    const isAlreadyApproved = Boolean(targetedRecommendation) && true === targetedRecommendation.isApproved;
    const isAlreadyRefused = Boolean(targetedRecommendation) && false === targetedRecommendation.isApproved;
    const approvedRecommendation = isAlreadyApproved ? targetedRecommendation : null;
    // Dossier validé dont l'enveloppe eSigne est en composition : le validateur peut choisir de le relancer
    const canRelaunch = isAlreadyApproved && 'Envelope_Signature_COMPOSITION' === approvedRecommendation.statusEnvelope;

    // Le lien du mail cible une demande précise via l'id de ses recommandations : on ne conserve
    // que celle(s)-ci pour valider une demande à la fois (sans le paramètre, on affiche tout).
    if (recommendationId) {
        recommendationCif = recommendationCif.filter(({ id }) => id === recommendationId);
    }

    useEffect(() => {
        if ((null === isErrors || isErrors === undefined)) {
            setIsValuesCompleted(true);
        } else {
            setIsValuesCompleted(false);
        }
    }, [isErrors]);

    if (isAlreadyRefused || (canRelaunch && relaunchConfirmed !== recommendationId)) {
        return <ProcessedRequest {...props} ripId={rip.id} isRefused={isAlreadyRefused} />;
    }

    const isNotApproved = 'no' === isApproved;
    const hasLodeom = recommendationCif && recommendationCif.some(({ type }) => 'LODEOMCIF' === type);
    const scpiRecommendation = recommendationCif && recommendationCif.find(({ type }) => SCPI_CIF_TYPES.includes(type));
    const hasScpi = Boolean(scpiRecommendation);

    if (!hasLodeom && !hasScpi) {
        return null;
    }

    // Un seul produit par demande : LODEOM ou un type SCPI. Détermine la clé de stockage / le template eSigne.
    const productType = hasLodeom ? 'LODEOMCIF' : scpiRecommendation.type;

    const isApprovedYes = 'yes' === isApproved;
    const showESigne = isApprovedYes && (!hasScpi || isSimulationComplete);

    const valuesCompleted = hasScpi ? isSimulationComplete : isValuesCompleted;

    const isValidateButtonDisabled = !isNotApproved;

    return (
        <RipForm {...props} id={rip.id} hideBackButton enveloppeESigneCreated={isNotApproved || isGenerated} nextButtonContent="Valider" nextButtonDisabled={isValidateButtonDisabled}>
            <Download rip={rip} isInstanceTheseis={instanceTheseis} />
            <Recommendations rip={rip} recommendationCif={recommendationCif} />
            {hasLodeom && <EnveloppeResponse />}
            <ChoiceContact rip={rip} target={target} hasScpi={hasScpi} />
            {hasLodeom && <RipTaxIncomes ripFromRecommendation={rip} />}
            <ChoiceResponse rip={rip} />

            {isApprovedYes && hasScpi && (
                <SCPISimulation
                    recommendationCif={recommendationCif}
                    ripId={rip.id}
                    target={target}
                    onSimulationChecked={setIsSimulationComplete}
                    recommendationId={recommendationId}
                />
            )}

            {showESigne && (
                <ESigne
                    rip={rip}
                    isValuesCompleted={valuesCompleted}
                    setIsGenerated={setIsGenerated}
                    approvalCifBodyForm={approvalCifBodyForm}
                    productType={productType}
                    recommendationId={recommendationId}
                />
            )}

            <Comment />
            <p>Cliquez sur &quot;Valider&quot; pour terminer et notifier le conseiller. Cette fonction sera disponible uniquement apr&egrave;s g&eacute;n&eacute;ration et v&eacute;rification des donn&eacute;es du document de conseil CIF sur eSigne</p>
        </RipForm>
    );
};

FormInner.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            approval: PropTypes.string,
        }),
    }),
    rip: PropTypes.shape({
        id: PropTypes.string,
        recommendationsCIF: PropTypes.shape({}),
        recommendationsCIFClientAndPartner: PropTypes.shape({}),
        partner: PropTypes.shape({
            recommendationsCIF: PropTypes.shape({}),
        }),
    }).isRequired,
    session: PropTypes.shape({
        instanceTheseis: PropTypes.bool,
    }).isRequired,
    params: PropTypes.shape({
        target: PropTypes.string,
        recommendationId: PropTypes.string,
    }),
    location: PropTypes.shape({
        search: PropTypes.string,
    }),
    isErrors: PropTypes.shape({
    }).isRequired,
    isApproved: PropTypes.string,
    relaunchConfirmed: PropTypes.string,
    approvalCifBodyForm: PropTypes.shape({
    }),

};

const Form = flowRight([
    withRouter,
    withSession,
    dataProvider({
        id: ({ params: { id } }) => id,
        type: 'rip',
        wait: true,
    }),
    reduxForm({
        form: 'cif-approval',
        initialValues: { relaunch: 'no', relaunchConfirmed: null },
        validate: validateIncomesForReco,
        destroyOnUnmount: false,
        onSubmit: (
            body,
            dispatch,
            {
                rip,
                params: { id, target: urlTarget, recommendationId },
            },
        ) => {
            const isCifScpi = getTypeRecommendations(rip, urlTarget);

            return POST(`/api/rips/${id}/${recommendationId}/sendAdvisorCIFNotification`, {
                body: {
                    ...body,
                    isCifScpi,
                    urlTarget,
                },
            });
        },
        onSubmitSuccess: alertEmail,
    }),
    connect(null, (dispatch) => ({
        load: (ripId) => dispatch(getIsValidator(ripId)),
        // Le formulaire n'est pas détruit au démontage : NON redevient le choix par défaut,
        // sauf si la relance de cette demande a déjà été confirmée (ex. retour depuis une simulation)
        resetRelaunch: (recommendationId) => dispatch((_, getState) => {
            const { relaunchConfirmed } = getFormValues('cif-approval')(getState()) || {};
            if (relaunchConfirmed !== recommendationId) {
                dispatch(change('cif-approval', 'relaunch', 'no'));
                dispatch(change('cif-approval', 'relaunchConfirmed', null));
            }
        }),
    })),
    lifecycle({
        componentDidMount() {
            this.props.load(this.props.rip.id);
            this.props.resetRelaunch(this.props.params.recommendationId);
        },
    }),
    formValues('isApproved', 'relaunchConfirmed'),
    connect((state) => ({
        isValidator: state.isValidator,
        isErrors: state.form['cif-approval'].syncErrors,
        approvalCifBodyForm: getFormValues('cif-approval')(state),
    })),
    branch(({ isValidator }) => !isValidator, renderNothing),
])(FormInner);

export default Form;
